import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Badge, Dropdown, H3, Icon, InlineNotification, Link as NucleusLink, Spinner, Tooltip, useDialog, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { PageHeader, PageWrapper, ReportingCard, ReportingGrid, useBooleanState } from 'libs.react';
import { ChartEmptyState } from './chart_empty_state.component';
import { ChartPreviewContent } from './chart_preview_content.component';
import { performAtomicVersionUpgrade } from '../../../utils/atomic_version_upgrade.utils';
import { buildPivotConfigFromState, buildQueryFromState, useChartQuery, useCharts, useDatasetVersionCheck, } from '../../hooks';
import { collectChartFieldReferences, getBuilderChartTypeFromData, getPersistedChartType, getStatusBadgeVariant, getStatusLabel, hasValidNumberChartLabels, scrubChartDataFieldReferences, toColumnConfig, } from '../../utils';
import { ChangeStatusModal } from '../change_status_modal/change_status_modal.component';
import { EditChartDetailsModal } from '../edit_chart_details_modal/edit_chart_details_modal.component';
import { ReviewDatasetVersionModal } from '../review_dataset_version_modal/review_dataset_version_modal.component';
import { DEFAULT_ROWS_PER_PAGE } from '../settings_panel/chart_type_section.component';
import { SettingsPanel } from '../settings_panel/settings_panel.component';
export const ChartDetail = () => {
    const { chartId } = useParams();
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { fetchChartById, updateChartData, cloneChart, archiveChart, isLoading: isApiLoading } = useCharts();
    const dialog = useDialog();
    const [chart, setChart] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(true);
    const [isChangeStatusModalOpen, openChangeStatusModal, closeChangeStatusModal] = useBooleanState();
    const [isEditDetailsModalOpen, openEditDetailsModal, closeEditDetailsModal] = useBooleanState();
    const [isReviewVersionsModalOpen, openReviewVersionsModal, closeReviewVersionsModal] = useBooleanState();
    // Settings panel state derived from chart data
    const [settingsState, setSettingsState] = useState({
        chartType: 'table',
    });
    // ── Dirty tracking — compare non-data settings against last saved snapshot ─
    // Data section fields (study, env, columns, axes, order) are tied to the Run
    // flow and excluded from the Save dirty check.
    const [savedSettingsSnapshot, setSavedSettingsSnapshot] = useState('');
    // Only study/env are runtime context — everything else is part of the chart config
    const EXCLUDED_FROM_DIRTY_CHECK = ['studyId', 'testEnvironmentId'];
    const getSaveableSettings = useCallback((state) => {
        const filtered = {};
        for (const key of Object.keys(state)) {
            if (!EXCLUDED_FROM_DIRTY_CHECK.includes(key)) {
                filtered[key] = state[key];
            }
        }
        return JSON.stringify(filtered);
    }, []);
    const isDirty = useMemo(() => {
        if (!savedSettingsSnapshot) {
            return false;
        }
        return getSaveableSettings(settingsState) !== savedSettingsSnapshot;
    }, [settingsState, getSaveableSettings, savedSettingsSnapshot]);
    // ── Chart query execution ────────────────────────────────────────────────
    const { resultSet, isLoading: isQueryLoading, error: queryError, total: queryTotal, hasRun, executeQuery, resetResults, } = useChartQuery({ state: settingsState, fallbackQuery: chart?.data?.query, chartResource: chart });
    // ── Track whether query-affecting fields changed since last run ─────
    const buildQuerySnapshot = useCallback((s) => JSON.stringify({
        chartType: s.chartType,
        orientation: s.orientation,
        xAxis: s.xAxis?.key,
        yAxis: s.yAxis?.key,
        splitBy: s.splitBy?.key,
        additionalMeasures: (s.additionalMeasures || []).map((m) => m.key),
        columns: (s.columns || []).map((c) => c.key),
        numberValues: (s.numberValues || []).map((v) => v.value.key),
        filters: s.filters || [],
        orderRules: s.orderRules || [],
        defaultSort: s.defaultSort,
        defaultRowsPerPage: s.defaultRowsPerPage,
        dotSizeMapping: s.dotSizeMapping,
        dotSizeField: s.dotSizeField?.key,
    }), []);
    const [lastRunQuerySnapshot, setLastRunQuerySnapshot] = useState('');
    // Snapshot of the settings used for the most recent Run. The preview reads
    // from this snapshot rather than the live settingsState so that downstream
    // chart rendering (especially scatter/bubble with thousands of points) does
    // not re-run on every keystroke or settings panel toggle. Users see the
    // updated preview after they click Run again.
    const [previewSettings, setPreviewSettings] = useState(null);
    const renderSettings = previewSettings ?? settingsState;
    // The preview's pivotConfig depends only on the snapshotted settings, so
    // memo it to keep the prop reference stable between settings-panel keystrokes
    // (which would otherwise tear through React.memo on ChartPreviewContent).
    const previewPivotConfig = useMemo(() => buildPivotConfigFromState(renderSettings) || chart?.data?.pivotConfig, [renderSettings, chart?.data?.pivotConfig]);
    // Table handlers capture settingsState via a ref so they don't re-create
    // (and re-tear-down memos downstream) on every keystroke.
    const settingsStateRef = useRef(settingsState);
    useEffect(() => {
        settingsStateRef.current = settingsState;
    }, [settingsState]);
    const needsReRun = useMemo(() => {
        if (!hasRun) {
            return false;
        }
        return buildQuerySnapshot(settingsState) !== lastRunQuerySnapshot;
    }, [hasRun, settingsState, buildQuerySnapshot, lastRunQuerySnapshot]);
    // Clear stale results when query-shaping fields change so the previous
    // result set isn't re-pivoted with an incompatible config.
    const prevChartTypeRef = useRef(settingsState.chartType);
    const prevSplitByRef = useRef(settingsState.splitBy?.key);
    useEffect(() => {
        const chartTypeChanged = prevChartTypeRef.current !== settingsState.chartType;
        const splitByChanged = prevSplitByRef.current !== settingsState.splitBy?.key;
        if (chartTypeChanged || splitByChanged) {
            prevChartTypeRef.current = settingsState.chartType;
            prevSplitByRef.current = settingsState.splitBy?.key;
            resetResults();
        }
    }, [settingsState.chartType, settingsState.splitBy?.key, resetResults]);
    useEffect(() => {
        if (!chartId) {
            navigate('/library/charts');
            return;
        }
        let isMounted = true;
        const loadChart = async () => {
            setIsLoading(true);
            try {
                const result = await fetchChartById(chartId);
                if (isMounted && result) {
                    setChart(result);
                    if (result.data) {
                        const initialState = {
                            chartType: getBuilderChartTypeFromData(result.data),
                            orientation: result.data?.type === 'horizontalBar' ? 'horizontal' : result.data?.orientation,
                            chartTitle: result.data?.title || '',
                            showLabels: result.data?.showLabels,
                            showLegend: result.data?.showLegend,
                            showArea: result.data?.showArea,
                            showDots: result.data?.showDots,
                            // Labels
                            xTitle: result.data?.xTitle,
                            yTitle: result.data?.yTitle,
                            labelOverrides: result.data?.labelOverrides,
                            // Colors
                            palette: result.data?.palette,
                            conditionalFormats: result.data?.conditionalFormats,
                            // Filters
                            filters: result.data?.filters,
                            // Data (columns & ordering)
                            columns: result.data?.columns,
                            numberValues: result.data?.numberValues,
                            xAxis: toColumnConfig(result.data?.xAxis),
                            yAxis: toColumnConfig(result.data?.yAxis),
                            splitBy: toColumnConfig(result.data?.splitBy, { role: 'dimension' }),
                            additionalMeasures: result.data?.additionalMeasures,
                            dimensionOrder: result.data?.dimensionOrder,
                            measureOrder: result.data?.measureOrder,
                            orderRules: result.data?.orderRules,
                            // Sort (table default sort) — normalize legacy single-object format to array
                            defaultSort: Array.isArray(result.data?.defaultSort)
                                ? result.data.defaultSort
                                : result.data?.defaultSort
                                    ? [result.data.defaultSort]
                                    : undefined,
                            // Table-specific
                            defaultRowsPerPage: result.data?.defaultRowsPerPage,
                            // Scatter/bubble
                            dotSizeMapping: result.data?.dotSizeMapping,
                            dotSizeField: result.data?.dotSizeField,
                            dotSizeConstant: result.data?.dotSizeConstant,
                            normalizeBubbleSize: result.data?.normalizeBubbleSize,
                            bubbleSizeMinOverride: result.data?.bubbleSizeMinOverride,
                            bubbleSizeMaxOverride: result.data?.bubbleSizeMaxOverride,
                            // Info card
                            cardInfo: result.data?.cardInfo,
                        };
                        setSettingsState((prev) => ({ ...prev, ...initialState }));
                        // Snapshot the initial state as the "saved" baseline
                        // Use setTimeout to ensure settingsState has been updated
                        setTimeout(() => {
                            setSavedSettingsSnapshot(getSaveableSettings({ ...settingsState, ...initialState }));
                        }, 0);
                    }
                }
                else if (isMounted) {
                    navigate('/library/charts');
                }
            }
            catch (error) {
                if (isMounted) {
                    navigate('/library/charts');
                }
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        loadChart();
        return () => {
            isMounted = false;
        };
    }, [chartId]);
    // Set saved snapshot once chart and settingsState are both ready
    useEffect(() => {
        if (chart && !savedSettingsSnapshot) {
            setSavedSettingsSnapshot(getSaveableSettings(settingsState));
        }
    }, [chart, settingsState, getSaveableSettings]);
    const handleStatusChange = useCallback((updatedChart) => {
        if (updatedChart.id !== chartId) {
            // A new chart was created (via clone or create new version) — navigate to it
            navigate(`/library/charts/${updatedChart.id}`);
        }
        else {
            setChart(updatedChart);
        }
    }, [chartId, navigate]);
    const handleDetailsSave = useCallback((updatedChart) => {
        const oldCubeConfigId = chart?.cubeConfigId || chart?.data?.cubeConfigId;
        const newCubeConfigId = updatedChart.cubeConfigId || updatedChart.data?.cubeConfigId;
        setChart(updatedChart);
        if (oldCubeConfigId !== newCubeConfigId) {
            setSettingsState((prev) => ({
                chartType: prev.chartType,
                studyId: prev.studyId,
                testEnvironmentId: prev.testEnvironmentId,
                chartTitle: prev.chartTitle,
                showLabels: prev.showLabels,
                showLegend: prev.showLegend,
                showArea: prev.showArea,
                showDots: prev.showDots,
                numberValues: prev.numberValues,
                cardInfo: prev.cardInfo,
            }));
            setSavedSettingsSnapshot('');
        }
    }, [chart]);
    const handleSettingsChange = useCallback((newState) => {
        setSettingsState(newState);
    }, []);
    // ── Table pagination and sort state (for TableChart) ─────────────────────
    const defaultPageSize = settingsState.defaultRowsPerPage ?? DEFAULT_ROWS_PER_PAGE;
    const [tablePaginationState, setTablePaginationState] = useState({ pageIndex: 0, pageSize: defaultPageSize });
    const [tableSortingConfig, setTableSortingConfig] = useState({});
    // ── Run handler — preview only, no persistence ─────────────────────────
    const handleRun = useCallback(async () => {
        if (!chart) {
            return;
        }
        setLastRunQuerySnapshot(buildQuerySnapshot(settingsState));
        setPreviewSettings(settingsState);
        const pageSize = settingsState.defaultRowsPerPage ?? DEFAULT_ROWS_PER_PAGE;
        setTablePaginationState({ pageIndex: 0, pageSize });
        setTableSortingConfig({});
        await executeQuery({ pageSize });
    }, [chart, executeQuery, settingsState, buildQuerySnapshot]);
    // ── Convert ConditionalFormatRule[] → ColumnStyle[] for data_reports ────
    // The data_reports rendering system reads the `style` field (ColumnStyle[])
    // whereas the settings panel works with `conditionalFormats`. We convert on
    // save so charts render correctly in both the builder preview and the
    // data_reports dashboard.
    //
    // Rules sharing a `columnName` are merged into a single `ColumnStyle` with
    // concatenated conditions. The settings panel stores one rule per visual
    // row (so on-screen order stays stable across edits), while the render
    // path's `getColumnStyle` looks up styles with `Array.find` and would
    // otherwise silently drop every rule past the first for a given column.
    const buildStyleFromConditionalFormats = useCallback((formats) => {
        if (!formats || formats.length === 0) {
            return undefined;
        }
        const styleByColumn = new Map();
        for (const rule of formats) {
            const key = rule.columnName ?? '';
            const existing = styleByColumn.get(key);
            if (existing) {
                existing.conditions = [...(existing.conditions ?? []), ...(rule.conditions ?? [])];
            }
            else {
                styleByColumn.set(key, {
                    columnName: rule.columnName,
                    property: rule.property,
                    type: rule.type ?? (settingsState.chartType === 'table' ? 'cell' : undefined),
                    showValue: rule.showValue,
                    conditions: [...rule.conditions],
                    defaultValue: rule.defaultValue,
                });
            }
        }
        return Array.from(styleByColumn.values());
    }, [settingsState.chartType]);
    // ── Save handler — persist the full chart config to the backend ─────────
    const handleSave = useCallback(async () => {
        if (!chart) {
            return;
        }
        if (settingsState.chartType === 'number' && !hasValidNumberChartLabels(settingsState.numberValues)) {
            addNotification({
                title: translate('Label is required'),
                subtitle: translate('Enter a label for each value before saving the chart.'),
                type: 'error',
            });
            return;
        }
        const query = buildQueryFromState(settingsState) || chart.data?.query;
        const pivotConfig = buildPivotConfigFromState(settingsState) || chart.data?.pivotConfig;
        const persistedChartType = getPersistedChartType(settingsState);
        // Preserve cubeConfigId/Key that were stored in the original data
        const existingData = chart.data;
        const savedCubeConfigId = chart.cubeConfigId || existingData?.cubeConfigId || undefined;
        const savedCubeConfigKey = chart.cubeConfigKey || existingData?.cubeConfigKey || undefined;
        await updateChartData(chart.id, {
            type: persistedChartType,
            builderChartType: settingsState.chartType,
            title: settingsState.chartTitle || '',
            showLabels: settingsState.showLabels,
            showLegend: settingsState.showLegend,
            showArea: settingsState.showArea,
            showDots: settingsState.showDots,
            query,
            pivotConfig,
            // Preserve dataset association
            cubeConfigId: savedCubeConfigId,
            cubeConfigKey: savedCubeConfigKey,
            // Labels
            xTitle: settingsState.xTitle,
            yTitle: settingsState.yTitle,
            labelOverrides: settingsState.labelOverrides,
            // Colors
            palette: settingsState.palette,
            conditionalFormats: settingsState.conditionalFormats,
            // Style — ColumnStyle[] for data_reports dashboard rendering compatibility
            // (including number charts, which evaluate per-measure conditions as text color)
            style: buildStyleFromConditionalFormats(settingsState.conditionalFormats),
            // Filters
            filters: settingsState.filters,
            // Data (columns & ordering)
            columns: settingsState.columns,
            numberValues: settingsState.numberValues,
            xAxis: settingsState.xAxis,
            yAxis: settingsState.yAxis,
            splitBy: settingsState.splitBy,
            additionalMeasures: settingsState.additionalMeasures,
            orientation: settingsState.orientation,
            dimensionOrder: settingsState.dimensionOrder,
            measureOrder: settingsState.measureOrder,
            orderRules: settingsState.orderRules,
            // Sort (table default sort)
            defaultSort: settingsState.defaultSort,
            // Table-specific
            defaultRowsPerPage: settingsState.defaultRowsPerPage,
            // Scatter/bubble
            dotSizeMapping: settingsState.dotSizeMapping,
            dotSizeField: settingsState.dotSizeField,
            dotSizeConstant: settingsState.dotSizeConstant,
            normalizeBubbleSize: settingsState.normalizeBubbleSize,
            bubbleSizeMinOverride: settingsState.bubbleSizeMinOverride,
            bubbleSizeMaxOverride: settingsState.bubbleSizeMaxOverride,
            // Info card
            cardInfo: settingsState.cardInfo,
        });
        // Update the saved snapshot so isDirty resets to false
        setSavedSettingsSnapshot(getSaveableSettings(settingsState));
    }, [chart, settingsState, updateChartData, getSaveableSettings, addNotification, translate]);
    // ── Table page change handler (re-fetches data for the new page) ────────
    const handlePageChange = useCallback((pageIndex, pageSize) => {
        setTablePaginationState({ pageIndex, pageSize });
        setPreviewSettings(settingsStateRef.current);
        executeQuery({ pageIndex, pageSize, order: tableSortingConfig });
    }, [executeQuery, tableSortingConfig]);
    // ── Table sort change handler ───────────────────────────────────────────
    const handleSortChange = useCallback((config) => {
        setTableSortingConfig(config);
        setTablePaginationState((prev) => ({ ...prev, pageIndex: 0 }));
        setPreviewSettings(settingsStateRef.current);
        executeQuery({
            pageIndex: 0,
            pageSize: tablePaginationState.pageSize,
            order: config,
        });
    }, [executeQuery, tablePaginationState.pageSize]);
    const toggleSettingsPanel = useCallback(() => {
        setIsSettingsPanelOpen((prev) => !prev);
    }, []);
    const handleShowInfoCard = useCallback(async () => {
        if (!settingsState.cardInfo) {
            return;
        }
        await dialog.open({
            title: translate('Information card'),
            body: (_jsx("div", { className: 'max-h-[500px] overflow-auto', dangerouslySetInnerHTML: { __html: settingsState.cardInfo } })),
            actionButtons: [{ label: translate('Close') }],
        });
    }, [settingsState.cardInfo, dialog, translate]);
    const breadcrumbsPages = useMemo(() => [
        { name: translate('Charts'), path: '/library/charts' },
        { name: chart?.name || '', path: `/library/charts/${chartId}` },
    ], [chart?.name, chartId, translate]);
    const isEditable = useMemo(() => chart?.status === 'DRAFT', [chart?.status]);
    // cubeConfigId/Key may live at root level or inside chart.data depending on the API response
    const chartData = chart?.data;
    const datasetId = (chart?.cubeConfigId || chartData?.cubeConfigId || '');
    const datasetKey = (chart?.cubeConfigKey || chartData?.cubeConfigKey || '');
    const { isNewerDatasetAvailable } = useDatasetVersionCheck({
        cubeConfigId: datasetId,
        cubeConfigKey: datasetKey,
    });
    const handleApplyDatasetUpdate = useCallback(async (newDatasetId, availableFieldNames) => {
        if (!chart) {
            return;
        }
        // Strip chart settings (xAxis/yAxis/columns/query.*/etc.) of any field
        // references the new dataset version no longer exposes. We bypass
        // updateChartDetails (which blanket-wipes the data when the dataset
        // changes) in favour of a selective scrub so valid fields are
        // preserved across the upgrade. The new cubeConfigId/Key live both
        // at root and inside `data` — mirror that here for consistency with
        // how the rest of the codebase reads them.
        const scrubbedData = {
            ...(scrubChartDataFieldReferences(chart.data, availableFieldNames) || {}),
            cubeConfigId: newDatasetId,
            cubeConfigKey: datasetKey,
        };
        // PUBLISHED charts cannot be edited in place — clone with CREATE_VERSION
        // so a new DRAFT version is created, archive the current one, then
        // navigate to the new chart. Mirrors the dashboard's review-versions
        // pattern (libs/data/src/dashboards/components/dashboard_detail).
        //
        // SSP-4186: the create + archive must be atomic. If the archive fails
        // after the new version is created, both versions stay active and the
        // Library shows duplicate rows. performAtomicVersionUpgrade retries the
        // archive and, failing that, rolls back the new version so at most one
        // version per key is ever active.
        if (chart.status === 'PUBLISHED') {
            const result = await performAtomicVersionUpgrade({
                previous: chart,
                createVersion: () => cloneChart({ ...chart, data: scrubbedData }, 'CREATE_VERSION', { cubeConfigId: newDatasetId, cubeConfigKey: datasetKey }, { silent: true }),
                archive: (resource) => archiveChart(resource.id, { silent: true }),
            });
            if (result.outcome === 'create_failed') {
                addNotification({
                    title: translate('Version upgrade failed'),
                    subtitle: translate('Could not create the new chart version. No changes were made.'),
                    type: 'error',
                });
                return;
            }
            if (result.outcome === 'archive_failed') {
                addNotification({
                    title: translate('Version upgrade failed'),
                    subtitle: translate('Could not archive the previous version. No changes were kept.'),
                    type: 'error',
                });
                return;
            }
            addNotification({
                title: translate('Chart updated to latest dataset version'),
                type: 'success',
            });
            navigate(`/library/charts/${result.newResource.id}`);
            return;
        }
        const updated = await updateChartData(chart.id, scrubbedData);
        // Force the new cubeConfigId/Key onto the root-level fields too. The
        // server may not auto-sync root with data, so without this patch
        // useDatasetVersionCheck would re-run with the stale root-level id and
        // the banner would stay visible until a manual refresh.
        setChart({ ...updated, cubeConfigId: newDatasetId, cubeConfigKey: datasetKey });
        // The settings sidebar reads from `settingsState`, not directly from
        // `chart.data`. Re-derive the panel state from the freshly-scrubbed
        // chart so ghost dimensions/measures the new dataset dropped don't
        // linger in the UI after Apply.
        if (updated.data) {
            const refreshed = {
                chartType: getBuilderChartTypeFromData(updated.data),
                orientation: updated.data.type === 'horizontalBar' ? 'horizontal' : updated.data.orientation,
                chartTitle: updated.data.title || '',
                showLabels: updated.data.showLabels,
                showLegend: updated.data.showLegend,
                showArea: updated.data.showArea,
                showDots: updated.data.showDots,
                xTitle: updated.data.xTitle,
                yTitle: updated.data.yTitle,
                labelOverrides: updated.data.labelOverrides,
                palette: updated.data.palette,
                conditionalFormats: updated.data.conditionalFormats,
                filters: updated.data.filters,
                columns: updated.data.columns,
                numberValues: updated.data.numberValues,
                xAxis: toColumnConfig(updated.data.xAxis),
                yAxis: toColumnConfig(updated.data.yAxis),
                splitBy: toColumnConfig(updated.data.splitBy, { role: 'dimension' }),
                additionalMeasures: updated.data.additionalMeasures,
                dimensionOrder: updated.data.dimensionOrder,
                measureOrder: updated.data.measureOrder,
                orderRules: updated.data.orderRules,
                defaultSort: Array.isArray(updated.data.defaultSort)
                    ? updated.data.defaultSort
                    : updated.data.defaultSort
                        ? [updated.data.defaultSort]
                        : undefined,
                defaultRowsPerPage: updated.data.defaultRowsPerPage,
                dotSizeMapping: updated.data.dotSizeMapping,
                dotSizeField: toColumnConfig(updated.data.dotSizeField),
                dotSizeConstant: updated.data.dotSizeConstant,
                normalizeBubbleSize: updated.data.normalizeBubbleSize,
                bubbleSizeMinOverride: updated.data.bubbleSizeMinOverride,
                bubbleSizeMaxOverride: updated.data.bubbleSizeMaxOverride,
                cardInfo: updated.data.cardInfo,
            };
            setSettingsState((prev) => ({ ...prev, ...refreshed }));
            setSavedSettingsSnapshot('');
        }
    }, [chart, updateChartData, cloneChart, archiveChart, navigate, datasetKey, addNotification, translate]);
    const headerOptions = useMemo(() => {
        if (!chart) {
            return [];
        }
        return [
            {
                name: translate('Change status'),
                onClick: openChangeStatusModal,
                variant: 'secondary',
                disabled: isApiLoading || isDirty,
            },
            {
                // Editable charts edit details; non-DRAFT charts can view details read-only.
                name: isEditable ? translate('Edit chart details') : translate('View Chart Details'),
                onClick: openEditDetailsModal,
                variant: 'secondary',
                disabled: isApiLoading,
            },
            {
                name: isDirty ? `${translate('Save')} *` : translate('Save'),
                onClick: handleSave,
                variant: 'primary',
                disabled: isApiLoading || !isDirty,
            },
        ];
    }, [chart, translate, openChangeStatusModal, openEditDetailsModal, handleSave, isApiLoading, isDirty, isEditable]);
    if (isLoading || !chart) {
        return (_jsx("section", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) }));
    }
    // Only show the chart title in the preview card when there's actual data to display
    const chartTitle = resultSet ? settingsState.chartTitle || '' : '';
    return (_jsxs("div", { className: 'flex h-full overflow-hidden', children: [isSettingsPanelOpen ? (_jsx(SettingsPanel, { state: settingsState, onChange: handleSettingsChange, onRun: handleRun, isRunning: isQueryLoading, isEditable: isEditable, chart: chart, onToggle: toggleSettingsPanel, needsReRun: needsReRun })) : (_jsx("div", { className: 'flex flex-col items-center border-r border-neutral-200 flex-shrink-0 bg-white py-4 px-3', children: _jsx(Tooltip, { placement: 'end', delayShow: 500, wrapperClassName: 'p-0', title: translate('Open sidebar'), children: _jsx("div", { className: 'inline-flex text-neutral-450 rounded-lg hover:text-primary-500 hover:bg-neutral-100 p-1 cursor-pointer', onClick: toggleSettingsPanel, children: _jsx(Icon, { name: 'sidebar', size: 'sm', color: 'current' }) }) }) })), _jsxs(PageWrapper, { className: 'min-w-0', children: [_jsx(PageHeader, { breadcrumbPages: breadcrumbsPages, LinkComponent: Link, title: _jsxs("div", { className: 'flex items-center gap-x-2', children: [_jsx(H3, { children: chart.name }), _jsx(Badge, { variant: getStatusBadgeVariant(chart.status), label: getStatusLabel(chart.status, translate) }), _jsx(Badge, { variant: 'common', label: `v${chart.version}` })] }), subtitle: chart.metadata?.description, options: headerOptions }), isNewerDatasetAvailable && (_jsx("div", { className: 'mb-6', children: _jsx(InlineNotification, { type: 'info', title: translate('New version available'), subtitle: translate("A newer version of this chart's dataset is available in the Library."), action: _jsx(NucleusLink, { href: '#', onClick: (e) => {
                                    e.preventDefault();
                                    openReviewVersionsModal();
                                }, children: translate('Review versions') }) }) })), !hasRun && !isQueryLoading ? (_jsx(ChartEmptyState, {})) : (
                    /* Chart Preview — shown after the query has been executed */
                    _jsx(ReportingGrid, { padding: 4, cards: [
                            {
                                card: (_jsx(ReportingCard, { title: chartTitle ? _jsx("div", { className: 'px-2', children: chartTitle }) : undefined, headerAction: settingsState.cardInfo ? (_jsx(Dropdown, { variant: 'kebab', position: 'bottom-end', items: [
                                            {
                                                id: 'info-card',
                                                content: translate('See info card'),
                                                onClick: handleShowInfoCard,
                                            },
                                        ] })) : undefined, children: _jsx(ChartPreviewContent, { chartType: renderSettings.chartType, resultSet: resultSet, isLoading: isQueryLoading, error: queryError, total: queryTotal, onPageChange: handlePageChange, tablePaginationState: tablePaginationState, setTableSortingConfig: handleSortChange, labelOverrides: renderSettings.labelOverrides, conditionalFormats: renderSettings.conditionalFormats, columns: renderSettings.columns, xAxis: renderSettings.xAxis, yAxis: renderSettings.yAxis, splitBy: renderSettings.splitBy, orientation: renderSettings.orientation, pivotConfig: previewPivotConfig, palette: renderSettings.palette, xTitle: renderSettings.xTitle, yTitle: renderSettings.yTitle, showLabels: renderSettings.showLabels, showLegend: renderSettings.showLegend, showArea: renderSettings.showArea, showDots: renderSettings.showDots, numberValues: renderSettings.numberValues, dimensionOrder: renderSettings.dimensionOrder, measureOrder: renderSettings.measureOrder, dotSizeMapping: renderSettings.dotSizeMapping, dotSizeField: renderSettings.dotSizeField, dotSizeConstant: renderSettings.dotSizeConstant, normalizeBubbleSize: renderSettings.normalizeBubbleSize, bubbleSizeMinOverride: renderSettings.bubbleSizeMinOverride, bubbleSizeMaxOverride: renderSettings.bubbleSizeMaxOverride, defaultSortedColumns: renderSettings.defaultSort }) })),
                                xSpan: 12,
                            },
                        ], rowHeight: 'base' })), isChangeStatusModalOpen && (_jsx(ChangeStatusModal, { chart: chart, onClose: closeChangeStatusModal, onStatusChange: handleStatusChange })), isEditDetailsModalOpen && (_jsx(EditChartDetailsModal, { isOpen: isEditDetailsModalOpen, onClose: closeEditDetailsModal, onSave: handleDetailsSave, chart: chart, isReadOnly: !isEditable })), isReviewVersionsModalOpen && (_jsx(ReviewDatasetVersionModal, { isOpen: isReviewVersionsModalOpen, onClose: closeReviewVersionsModal, cubeConfigId: datasetId, cubeConfigKey: datasetKey, chartStatus: chart.status, chartVersion: chart.version, chartFieldReferences: collectChartFieldReferences(chart), onApply: handleApplyDatasetUpdate }))] })] }));
};
